import { Center, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { NoResultsView } from '@apple/ui/data-table/controls/no-results-view';
import { BlockNavigation } from '@apple/ui/form/components/BlockNavigation';
import { useBreakpoints } from '@apple/ui/layouts/hooks/useBreakpoints';
import { StepLayout } from '@apple/ui/layouts/step';
import type { Guid } from '@apple/utils/primitive';

import { LineFilterBar } from '@apple/features/store-events/components/line-filter-bar';
import { ActionsTab } from '@apple/features/store-events/components/lines/actions-tab';
import { CategoryLinesTable } from '@apple/features/store-events/components/lines/category-lines-table';
import { useArchiveStoreEvent } from '@apple/features/store-events/hooks/use-archive-store-event';
import {
	EventProductsFormProvider,
	type ManualAddition,
	useEventProductsForm,
} from '@apple/features/store-events/hooks/use-event-products-form';
import { useSelectedCategories } from '@apple/features/store-events/hooks/use-selected-categories';
import {
	type CategoryLines,
	DEFAULT_STORE_EVENT_LINE_FILTERS,
	type StoreEventLineFilters,
	storeEventLineFiltersSchema,
} from '@apple/features/store-events/models/lines';
import { storeEventQueries } from '@apple/features/store-events/queries';

const searchSchema = storeEventLineFiltersSchema.partial();

export const Route = createFileRoute('/_authed/store-events/_steps/$storeEventId/products')({
	validateSearch: searchSchema,
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation('store-event');
	const { storeEventId } = Route.useParams();
	const initialFilters = Route.useSearch();
	const navigate = Route.useNavigate();
	const archiveMutation = useArchiveStoreEvent(storeEventId);
	const { mdOrLarger: showFilterBar } = useBreakpoints();

	const [filters, setFilters] = useState<StoreEventLineFilters>(() => ({
		storeEventId,
		...DEFAULT_STORE_EVENT_LINE_FILTERS,
		...initialFilters,
	}));

	const [storeEventQuery, linesByCategory, categories] = useQueries({
		queries: [
			storeEventQueries.getById(storeEventId),
			storeEventQueries.linesByCategory(filters),
			storeEventQueries.categories,
		],
	});

	const erpDetailsQuery = useQuery(
		storeEventQueries.lineFulfillmentInfo(
			storeEventId,
			linesByCategory.data?.items.flatMap(x => x.category.id) ?? [],
			storeEventQuery.data?.hasErpDetails ?? false,
		),
	);

	const { categoryIds } = useSelectedCategories(storeEventId);

	const { form, submitMutation } = useEventProductsForm(storeEventId);

	const [currentCategoryLines, setCurrentCategoryLines] = useState<CategoryLines[]>(
		linesByCategory.data?.items ?? [],
	);

	const [lastCategoryAdded, setLastCategoryAdded] = useState<Guid | null>(null);

	useEffect(() => {
		if (linesByCategory.data?.items) {
			setCurrentCategoryLines(linesByCategory.data.items);
		}
	}, [linesByCategory.data?.items]);

	const handleManualAddedLines = useCallback(
		(lastManualAddition: ManualAddition) => {
			setLastCategoryAdded(lastManualAddition.categoryId);

			const existingLines = linesByCategory.data?.items ?? [];
			// Add categories that exist only in the form (manualAdditions)
			const map = new Map<Guid, CategoryLines>(existingLines.map(x => [x.category.id, x]));
			for (const categoryId of Object.keys(form.getValues().manualAdditions) as Guid[]) {
				if (!map.has(categoryId)) {
					const newCategory = categories.data?.find(c => c.id === categoryId);
					map.set(categoryId, {
						category: {
							id: categoryId,
							title: newCategory?.title ?? '',
							imageUrl: newCategory?.imageUrl ?? '',
							labelImageUrl: newCategory?.labelImageUrl ?? '',
							quantity: 0,
						},
						items: [],
					} satisfies CategoryLines);
				}
			}
			const existingCategoryIds = new Set(existingLines.map(x => x.category.id));
			setCurrentCategoryLines([
				...existingLines,
				...Array.from(map.values()).filter(x => !existingCategoryIds.has(x.category.id)),
			]);
		},
		[form.getValues, categories.data, linesByCategory.data?.items],
	);

	return (
		<EventProductsFormProvider form={form}>
			<StepLayout
				queryErrors={[storeEventQuery.error, linesByCategory.error, erpDetailsQuery.error]}
				mutationErrors={[submitMutation.error, archiveMutation.error]}
				buttons={[
					{
						text: t('common:pagination.previousShort'),
						to: '/store-events/$storeEventId/categories',
						params: { storeEventId },
					},
					{
						text: t('common:buttons.save'),
						color: 'blue',
						variant: 'filled',
						hidden: !(
							storeEventQuery.data?.canBeSaved ||
							storeEventQuery.data?.canItemsBeAdded
						),
						loading: submitMutation.isPending,
						disabled: submitMutation.isPending,
						onClick: () =>
							submitMutation.mutateAsync(
								// The old site allowed "saving" the lines and notes in the "canItemsBeAdded" state
								// and also called an additional "saveStoreEvent" endpoint if the event "canBeSaved"
								storeEventQuery.data?.canBeSaved ? 'save' : 'modify',
								{
									onSuccess: () => navigate({ to: '/store-events' }),
								},
							),
					},
					{
						text: t('common:buttons.saveAndComplete'),
						bg: 'green',
						color: 'green',
						variant: 'filled',
						hidden: !storeEventQuery.data?.canBeCompleted,
						loading: submitMutation.isPending,
						disabled:
							submitMutation.isPending ||
							linesByCategory.data.nonZeroQuantityItems === 0,
						onClick: () => submitMutation.mutateAsync('complete'),
					},
					{
						text: t('common:buttons.submit'),
						color: 'green',
						variant: 'filled',
						hidden: !storeEventQuery.data?.canBeSubmitted,
						loading: submitMutation.isPending,
						disabled:
							submitMutation.isPending ||
							linesByCategory.data.nonZeroQuantityItems === 0,
						onClick: () =>
							modals.openConfirmModal({
								title: <Title order={5}>{t('submitConfirmation.title')}</Title>,
								children: (
									<Center py='md'>
										<Text>{t('submitConfirmation.message')}</Text>
									</Center>
								),
								labels: {
									confirm: t('common:buttons.confirm'),
									cancel: t('common:buttons.cancel'),
								},
								onConfirm: () =>
									submitMutation.mutate('submit', {
										onSuccess: () => navigate({ to: '/store-events' }),
									}),
								closeOnClickOutside: false,
							}),
					},
					{
						text: t('labels.archive'),
						hidden: !storeEventQuery.data?.canBeArchived,
						onClick: () =>
							archiveMutation.mutate(undefined, {
								onSuccess: () => {
									navigate({
										to: '/store-events',
									});
								},
							}),
						color: 'red',
						variant: 'filled',
						disabled:
							form.isDirty() || submitMutation.isPending || archiveMutation.isPending,
						loading: archiveMutation.isPending,
					},
				]}
			>
				<BlockNavigation
					condition={
						// Stop navigation when the form is dirty and not saved
						form.isDirty() && !(submitMutation.isPending || submitMutation.isSuccess)
					}
				/>

				{showFilterBar && (
					<LineFilterBar
						filters={filters}
						setFilters={filter => {
							setFilters(filter);
						}}
						categoryIds={categoryIds}
					/>
				)}

				<ActionsTab
					filters={filters}
					setFilters={setFilters}
					categoryIds={categoryIds}
					storeEventId={storeEventId}
					lineAdded={handleManualAddedLines}
					categoryLines={currentCategoryLines}
				>
					<Stack flex={1}>
						{!linesByCategory.isFetching && currentCategoryLines.length <= 0 ? (
							<NoResultsView />
						) : (
							currentCategoryLines.map(categoryLines => {
								return (
									<CategoryLinesTable
										storeEventId={storeEventId}
										key={categoryLines.category.id}
										categoryLines={categoryLines}
										isTarget={categoryLines.category.id === lastCategoryAdded}
										manualAdditions={
											form.getValues().manualAdditions[
												categoryLines.category.id
											]
										}
										erpDetails={
											!storeEventQuery.data?.hasErpDetails
												? []
												: erpDetailsQuery.data
										}
										erpDetailsLoading={erpDetailsQuery.isPending}
									/>
								);
							})
						)}
					</Stack>
				</ActionsTab>
			</StepLayout>
		</EventProductsFormProvider>
	);
}
